import { useRedirectLogOutUser } from "../../customHook/useRedirectLogOutUser";
import { useDispatch } from "react-redux";
import { SET_USER, SET_NAME } from "../../redux/features/auth/authSlice";
import { getUserProfiile, updateUserProfile, updatePassword } from "../../services/authService";
import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import ButtonLoading from '../../loader/ButtonLoader';
import { MdEditSquare, MdLock, MdPerson, MdEmail, MdPhone, MdBadge } from "react-icons/md";
import { Modal } from "bootstrap";

const profileInitial = {
  firstname:  "",
  fathername: "",
  email:      "",
  phone:      "",
  photo:      "",
  password:   "",
};

const passwordInitial = {
  oldPassword:     "",
  newPassword:     "",
  confirmPassword: "",
};

const dash = (v) => v || "—";

export const Profile = () => {
  useRedirectLogOutUser();

  const dispatch = useDispatch();

  const editModalRef     = useRef(null);
  const passwordModalRef = useRef(null);

  const [isLoadingProfile,  setIsLoadingProfile]  = useState(false);
  const [isLoadingPassword, setIsLoadingPassword] = useState(false);
  const [isLoadingData,     setIsLoadingData]     = useState(true);
  const [userData,          setUserData]          = useState(profileInitial);
  const [userImage,         setUserImage]         = useState(null);
  const [passwordForm,      setPasswordForm]      = useState(passwordInitial);

  useEffect(() => {
    async function load() {
      try {
        const profile = await getUserProfiile();
        setUserData(profile);
        dispatch(SET_USER(profile));
        dispatch(SET_NAME(profile.firstname));
      } catch {
        toast.error("Failed to load profile");
      } finally {
        setIsLoadingData(false);
      }
    }
    load();
  }, [dispatch]);

  const openEditModal = () => {
    document.activeElement?.blur();
    new Modal(editModalRef.current).show();
  };

  const openPasswordModal = () => {
    setPasswordForm(passwordInitial);
    document.activeElement?.blur();
    new Modal(passwordModalRef.current).show();
  };

  const handleInputChange = (e) => {
    setUserData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageChange = (e) => {
    setUserImage(e.target.files[0]);
  };

  const handlePasswordChange = (e) => {
    setPasswordForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!userData.password) return toast.error("Password is required to save changes");

    setIsLoadingProfile(true);
    try {
      let imageUrl = userData.photo;
      if (userImage && ["image/jpeg","image/png","image/jpg"].includes(userImage.type)) {
        const form = new FormData();
        form.append("file",          userImage);
        form.append("cloud_name",    "jokatrading");
        form.append("upload_preset", "dyiwtloq");
        const res  = await fetch("https://api.cloudinary.com/v1_1/jokatrading/image/upload", { method: "post", body: form });
        const data = await res.json();
        imageUrl   = data.url.toString();
      }
      const updated = {
        firstname:  userData.firstname,
        fathername: userData.fathername,
        email:      userData.email,
        phone:      userData.phone,
        photo:      imageUrl,
        password:   userData.password,
        userType:   userData.userType,
      };
      await updateUserProfile(updated);
      dispatch(SET_USER(updated));
      dispatch(SET_NAME(updated.firstname));
      setUserData({ ...updated, password: "" });
      setUserImage(null);
      toast.success("Profile updated successfully");
      Modal.getInstance(editModalRef.current).hide();
    } catch {
      toast.error("Profile update failed");
    }
    setIsLoadingProfile(false);
  };

  const savePassword = async (e) => {
    e.preventDefault();
    const { oldPassword, newPassword, confirmPassword } = passwordForm;
    if (!oldPassword)                     return toast.error("Enter your current password");
    if (newPassword.length < 6)           return toast.error("New password must be at least 6 characters");
    if (!newPassword || !confirmPassword) return toast.error("Please fill in all fields");
    if (newPassword !== confirmPassword)  return toast.error("Passwords do not match");

    setIsLoadingPassword(true);
    try {
      await updatePassword({ oldPassword, newPassword });
      toast.success("Password updated successfully");
      setPasswordForm(passwordInitial);
      Modal.getInstance(passwordModalRef.current).hide();
    } catch {
      toast.error("Failed to update password");
    }
    setIsLoadingPassword(false);
  };

  // Password strength 0–4
  const pwStrength = (() => {
    const p = passwordForm.newPassword;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 6)                       s++;
    if (p.length >= 10)                      s++;
    if (/[A-Z]/.test(p) || /[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p))             s++;
    return s;
  })();

  const pwStrengthLabel  = ['','Weak','Fair','Good','Strong'][pwStrength];
  const pwStrengthColor  = ['','danger','warning','primary','success'][pwStrength];

  const infoRows = [
    { icon: <MdPerson size={20}/>,  label: "First Name",    value: userData.firstname,  isRole: false },
    { icon: <MdPerson size={20}/>,  label: "Father Name",   value: userData.fathername, isRole: false },
    { icon: <MdEmail size={20}/>,   label: "Email Address", value: userData.email,      isRole: false },
    { icon: <MdPhone size={20}/>,   label: "Phone Number",  value: userData.phone,      isRole: false },
    { icon: <MdBadge size={20}/>,   label: "Role",          value: userData.userType,   isRole: true  },
  ];

  const avatarSrc = userImage
    ? URL.createObjectURL(userImage)
    : userData.photo ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(
        (userData.firstname || 'U') + '+' + (userData.fathername || '')
      )}&background=0d6efd&color=fff&size=110`;

  if (isLoadingData) {
    return (
      <div className="d-flex align-items-center justify-content-center min-vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" />
          <p className="text-muted">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-lg py-4">

      {/* ── EDIT PROFILE MODAL ──────────────────────────────── */}
      <div className="modal fade" ref={editModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">

            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10"
                  style={{ width: 48, height: 48 }}>
                  <MdEditSquare color="#0d6efd" size={24} />
                </div>
                <div>
                  <h4 className="modal-title fw-semibold mb-1 fs-4">Edit Profile</h4>
                  <p className="text-muted mb-0 fs-4">
                    Update your details and enter your password to save.
                  </p>
                </div>
              </div>
              <button className="btn-close ms-auto fs-4" data-bs-dismiss="modal" />
            </div>

            <form className="border-0" onSubmit={saveProfile}>
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 p-3">
                  <div className="row g-3">

                    {/* Avatar */}
                    <div className="col-12">
                      <div className="d-flex align-items-center gap-3 p-3 bg-white border rounded-3">
                        <img
                          src={avatarSrc}
                          alt="profile preview"
                          className="rounded-circle border border-2 border-primary flex-shrink-0"
                          style={{ width: 72, height: 72, objectFit: "cover" }}
                        />
                        <div className="flex-grow-1">
                          <label className="form-label fw-semibold fs-4">Profile Photo</label>
                          <input
                            className="form-control"
                            type="file"
                            accept="image/jpeg,image/png,image/jpg"
                            onChange={handleImageChange}
                          />
                          <div className="form-text fs-5">JPG or PNG, max 2MB</div>
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label fw-semibold fs-4">First Name</label>
                      <input className="form-control form-control-lg"
                        type="text" placeholder="First name"
                        name="firstname" value={userData.firstname || ""}
                        onChange={handleInputChange} />
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label fw-semibold fs-4">Father Name</label>
                      <input className="form-control form-control-lg"
                        type="text" placeholder="Father name"
                        name="fathername" value={userData.fathername || ""}
                        onChange={handleInputChange} />
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label fw-semibold fs-4">Email</label>
                      <input className="form-control form-control-lg"
                        type="email" placeholder="Email address"
                        name="email" value={userData.email || ""}
                        onChange={handleInputChange} />
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label fw-semibold fs-4">Phone Number</label>
                      <input className="form-control form-control-lg"
                        type="text" placeholder="Phone number"
                        name="phone" value={userData.phone || ""}
                        onChange={handleInputChange} />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold fs-4">
                        Current Password
                        <span className="text-danger ms-1 fw-normal fs-5">* required to save</span>
                      </label>
                      <input className="form-control form-control-lg"
                        type="password" placeholder="Enter your password to confirm"
                        autoComplete="off" name="password"
                        value={userData.password || ""}
                        onChange={handleInputChange} />
                    </div>

                  </div>
                </div>
              </div>

              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger flex-fill rounded-3 fw-semibold py-2"
                  data-bs-dismiss="modal">Cancel</button>
                {isLoadingProfile ? (
                  <ButtonLoading className="btn btn-primary flex-fill rounded-3 fw-semibold py-2" disabled>
                    Saving...
                  </ButtonLoading>
                ) : (
                  <button type="submit"
                    className="btn btn-primary flex-fill rounded-3 fw-semibold py-2">
                    Save Changes
                  </button>
                )}
              </div>
            </form>

          </div>
        </div>
      </div>

      {/* ── UPDATE PASSWORD MODAL ────────────────────────────── */}
      <div className="modal fade" ref={passwordModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">

            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-warning bg-opacity-15"
                  style={{ width: 48, height: 48 }}>
                  <MdLock color="#856404" size={24} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Update Password</h3>
                  <p className="text-muted mb-0 fs-4">
                    Choose a strong password of at least 6 characters.
                  </p>
                </div>
              </div>
              <button className="btn-close ms-auto fs-4" data-bs-dismiss="modal" />
            </div>

            <form className="border-0" onSubmit={savePassword}>
              <div className="modal-body py-3 border-0">
                <div className="bg-light rounded-3 p-3">
                  <div className="row g-3">

                    <div className="col-12">
                      <label className="form-label fw-semibold fs-4">Current Password</label>
                      <input className="form-control form-control-lg"
                        type="password" placeholder="Your current password"
                        name="oldPassword" autoComplete="current-password"
                        value={passwordForm.oldPassword}
                        onChange={handlePasswordChange} />
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label fw-semibold fs-4">New Password</label>
                      <input className="form-control form-control-lg"
                        type="password" placeholder="New password"
                        name="newPassword" autoComplete="new-password"
                        value={passwordForm.newPassword}
                        onChange={handlePasswordChange} />
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label fw-semibold fs-4">Confirm New Password</label>
                      <input className={`form-control form-control-lg ${
                        passwordForm.confirmPassword
                          ? passwordForm.newPassword === passwordForm.confirmPassword
                            ? "is-valid"
                            : "is-invalid"
                          : ""
                      }`}
                        type="password" placeholder="Confirm new password"
                        name="confirmPassword" autoComplete="new-password"
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordChange} />
                      {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                        <div className="invalid-feedback">Passwords do not match</div>
                      )}
                      {passwordForm.confirmPassword && passwordForm.newPassword === passwordForm.confirmPassword && (
                        <div className="valid-feedback">Passwords match ✓</div>
                      )}
                    </div>

                    {/* Strength bar */}
                    {passwordForm.newPassword && (
                      <div className="col-12">
                        <div className="d-flex gap-1 mb-1">
                          {[1,2,3,4].map(level => (
                            <div key={level} className="flex-fill rounded-pill"
                              style={{
                                height: "5px",
                                background: pwStrength >= level
                                  ? `var(--bs-${pwStrengthColor})`
                                  : "var(--bs-gray-300)",
                                transition: "background 0.25s",
                              }}
                            />
                          ))}
                        </div>
                        <span className={`badge bg-${pwStrengthColor} bg-opacity-15 text-${pwStrengthColor} rounded-pill px-2 py-1`}>
                          {pwStrengthLabel}
                        </span>
                      </div>
                    )}

                  </div>
                </div>
              </div>

              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger flex-fill rounded-3 fw-semibold py-2"
                  data-bs-dismiss="modal">Cancel</button>
                {isLoadingPassword ? (
                  <ButtonLoading className="btn btn-warning flex-fill rounded-3 fw-semibold py-2" disabled>
                    Updating...
                  </ButtonLoading>
                ) : (
                  <button type="submit"
                    className="btn btn-warning flex-fill rounded-3 fw-semibold py-2">
                    Update Password
                  </button>
                )}
              </div>
            </form>

          </div>
        </div>
      </div>

      {/* ── PROFILE VIEW ────────────────────────────────────── */}
      <div className="row justify-content-center">
        <div className="p-5">

          {/* ── Top card: banner + avatar + actions ── */}
          <div className="card border-0 rounded-4 shadow-sm mb-5 overflow-hidden">

            {/* Banner */}
            <div className="bg-primary mb-3" style={{ height: "95px" }} />

            <div className="card-body pt-0 px-4 pb-5">
              <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-end
                              justify-content-between gap-3"
                style={{ marginTop: "-44px" }}>

                {/* Avatar + name */}
                <div className="d-flex align-items-end gap-3">
                  <img
                    src={avatarSrc}
                    alt="profile"
                    className="rounded-circle border border-4 border-white shadow-sm flex-shrink-0"
                    style={{ width: 90, height: 90, objectFit: "cover" }}
                  />
                  <div className="pt-6">
                    <h3 className="fw-bold mb-1">
                      {dash(userData.firstname)} {dash(userData.fathername)}
                    </h3>
                    <span className={`badge rounded-pill px-3 py-2 fs-5 ${
                      userData.userType === "admin" ? "bg-primary" : "bg-secondary"
                    }`}>
                      {userData.userType || "—"}
                    </span>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    className="btn btn-warning rounded-3 d-flex align-items-center gap-2 fw-semibold"
                    onClick={openPasswordModal}>
                    <MdLock size={18} />
                    Change Password
                  </button>
                  <button
                    className="btn btn-primary rounded-3 d-flex align-items-center gap-2 fw-semibold"
                    onClick={openEditModal}>
                    <MdEditSquare size={20} />
                    Edit Profile
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Info card ── */}
          <div className="card border-0 rounded-4 shadow-sm">
            <div className="card-header bg-white border-bottom rounded-top-4 px-4 py-3
                            d-flex align-items-center gap-2">
              <MdPerson size={22} className="text-primary" />
              <h3 className="fw-semibold mb-0">Personal Information</h3>
            </div>
            <div className="card-body px-4 py-4">
              <div className="row g-3 border-3">
                {infoRows.map(({ icon, label, value, isRole }) => (
                  <div key={label} className="col-12 col-sm-6">
                    <div className="d-flex align-items-start gap-3 p-3 rounded-3">

                      {/* Icon bubble */}
                      <div className="d-flex align-items-center justify-content-center
                                      rounded-circle bg-primary bg-opacity-10 flex-shrink-0"
                        style={{ width: 40, height: 40 }}>
                        <span className="text-primary">{icon}</span>
                      </div>

                      <div>
                        <p className="text-muted fw-medium mb-1 fs-4"
                          style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}>
                          {label}
                        </p>
                        {isRole ? (
                          <span className={`badge rounded-pill px-3 py-2 fs-5 ${
                            value === "admin" ? "bg-primary" : "bg-secondary"
                          }`}>
                            {value || "—"}
                          </span>
                        ) : (
                          <p className="fw-semibold mb-0 fs-4">{dash(value)}</p>
                        )}
                      </div>

                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>

    </div>
  );
};